/**
 * ChatGPT journey — protocol `acp` (ADR-0159 §3).
 *
 * Discovery LIVE → Read menu LIVE → identity evidence panel (rendered
 * outside this builder) showing BOTH ChatGPT-User AND ChatGPT agent-
 * mode's RFC 9421 signature → ACP order-approval illustration (Sill's
 * shipped endpoint, ADR-0102) → SPT settlement illustration with the
 * explicit "Stripe owns SPT minting and redemption" caption → signed
 * audit.
 *
 * NEVER implies the dashboard or merchant can mint an SPT (ADR-0137).
 * The mandate signing card renders in the standard place (§4 tier gate);
 * this builder returns the phase list only.
 */

import { buildDiscoveryPhase, buildReadMenuPhase } from './discovery.js';
import type { JourneyContext, Phase } from './types.js';

export function buildChatGptJourney(ctx: JourneyContext): Phase[] {
  const { agent, live, installed, cardUrl, catalogUrl, liveProducts, cart } = ctx;
  return [
    buildDiscoveryPhase(agent, installed, cardUrl, live.card),
    buildReadMenuPhase(agent, installed, catalogUrl, live.catalog, liveProducts),
    // ── Identity gate — highlights the RFC 9421 signature story ──
    {
      id: 'identity',
      name: 'Identity check',
      tier: 'illustration',
      // Teaching detail (header names, JWKS URL, "only agentic browser…"
      // claim) lives in the IdentityEvidencePanel's grade-A facts — once
      // per journey, not per phase (ADR-0163 verbosity posture).
      narration:
        `${agent.name} proves who it is — agent mode signs its outbound HTTP requests per RFC 9421 with a vendor-published Ed25519 key. (Illustration — no signed handshake performed here.)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        // ADR-0163 §5 — a real JSON-shaped illustration of the RFC 9421
        // header set (the old body stuffed English prose into a JSON
        // string). The ILLUSTRATION tag carries the not-actually-
        // performed framing; the narration carries the English.
        body: '{ "signature_agent": "https://chatgpt.com", "signature_input": "sig1=(\\"@authority\\" \\"@method\\" \\"@path\\");keyid=…;alg=\\"ed25519\\"", "signature": "sig1=:…:" }',
        live: false,
      },
      ok: true,
    },
    // ── ACP order approval — real endpoint, illustration content ──
    {
      id: 'intent',
      name: 'Order approval',
      tier: 'illustration',
      narration: `${agent.name} posts an ACP order-approval request for 2 × ${cart.title}${cart.is_sample ? ' [sample]' : ''}. Sill's order-approval endpoint validates the order, checks it against your policy, and returns approved.`,
      endpoint: {
        method: 'POST',
        path: '/v1/order/approve',
        status: 200,
        ms: 0,
        body: cart.unit_minor !== null
          ? `{ "approved": true, "order_id": "ord_…", "amount_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
          : '{ "approved": true, "order_id": "ord_…" }',
        live: false,
      },
      ok: true,
    },
    // ── SPT settlement — the "Stripe owns SPT" caption is load-bearing ──
    {
      id: 'pay',
      name: 'SPT settlement',
      tier: 'illustration',
      // Merchant-legible (2026-07-05): no internal ADR citations in
      // customer-facing narration. Pinned substring "Stripe owns SPT
      // minting" preserved.
      narration: `Stripe owns SPT minting and redemption — only Stripe can issue the payment token, so neither ${agent.name} nor your store handles card details. Sill redeems the token for the charge and writes a signed audit record. (Illustration — no real charge here.)`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: cart.unit_minor !== null
          ? `{ "decision": "approved", "settled": true, "rail": "stripe.spt", "amount_minor": ${Math.round(cart.unit_minor * 2 * 1.15)} }`
          : '{ "decision": "approved", "settled": true, "rail": "stripe.spt" }',
        live: false,
      },
      ok: true,
    },
    {
      id: 'fulfill',
      name: 'Fulfillment',
      tier: 'illustration',
      narration: `Order placed, tracking issued, customer emailed — every step would land in your signed audit log.`,
      endpoint: {
        method: 'POST',
        path: '/v1/m/{site_key}/mandate',
        status: 200,
        ms: 0,
        body: '{ "fulfilled": true, "tracking": "1Z999AA1…" }',
        live: false,
      },
      ok: true,
    },
  ];
}
